// components/endowment/CategoryBrowser.jsx - Enhanced with metadata fetching
import React from "react";
import { useState, useRef, useEffect } from "react";
import { Form, Button, Dropdown, Spinner } from "react-bootstrap";
import { removeTxtExtension } from "../../services/endowment.service";
import { useAuth } from "../../contexts/AuthContext";
import { DOWNLOAD_ENDPOINTS } from "../../utils/constants";
import "./CategoryBrowser.css";

const CategoryBrowser = ({
  currentPath,
  onBackClick,
  onCategoryClick,
  searchTerm,
  onSearch,
  sortMethod,
  sortOrder,
  onSort,
  endowment,
  isLoading,
  onViewItem,
  onRemoveItem,
  onRemoveFolder
}) => {
  const { token } = useAuth();

  // State for managing which dropdown is open
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [clickTimeout, setClickTimeout] = useState(null);
  const dropdownRef = useRef(null);

  // State for metadata
  const [itemsWithMetadata, setItemsWithMetadata] = useState([]);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataCache, setMetadataCache] = useState(new Map());
  const [filesCurrentPath, setFilesCurrentPath] = useState([]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
    };
  }, [clickTimeout]);

  // Fetch metadata when category changes
  useEffect(() => {
    if (currentPath.length > 0 && endowment?.selectedItems) {
      fetchItemsMetadata();
    } else {
      setItemsWithMetadata([]);
    }
  }, [currentPath, filesCurrentPath, endowment?.selectedItems, token]);

  // Get bucket name from category
  const getBucketFromCategory = (category) => {
    const categoryLower = category.toLowerCase();
    switch (categoryLower) {
      case "files":
        return "files";
      case "passwords":
        return "passwords";
      case "banking":
        return "bankcards";
      case "blockchain":
        return "blockchain";
      default:
        return categoryLower;
    }
  };

  const parseFilesIntoFolders = (filePaths, currentFolderPath = []) => {
    const folders = new Map();
    const files = [];

    filePaths.forEach((filePath) => {
      const pathParts = filePath.split("/");

      // Check if file is in current folder context
      if (currentFolderPath.length === 0) {
        // At root level
        if (pathParts.length === 1) {
          // File is at root
          files.push(filePath);
        } else {
          // File is in a folder
          const folderName = pathParts[0];
          if (!folders.has(folderName)) {
            folders.set(folderName, {
              name: folderName,
              type: "folder",
              fullPath: folderName,
              fileCount: 0,
            });
          }
          folders.get(folderName).fileCount++;
        }
      } else {
        // In a subfolder
        const currentPathStr = currentFolderPath.join("/");
        if (filePath.startsWith(currentPathStr + "/")) {
          const remainingPath = filePath.substring(currentPathStr.length + 1);
          const remainingParts = remainingPath.split("/");

          if (remainingParts.length === 1) {
            // File is directly in current folder
            files.push(filePath);
          } else {
            // File is in a deeper subfolder
            const folderName = remainingParts[0];
            const folderFullPath = [...currentFolderPath, folderName].join("/");
            if (!folders.has(folderName)) {
              folders.set(folderName, {
                name: folderName,
                type: "folder",
                fullPath: folderFullPath,
                fileCount: 0,
              });
            }
            folders.get(folderName).fileCount++;
          }
        }
      }
    });

    return [...Array.from(folders.values()), ...files];
  };

  // Fetch metadata for multiple files
  const fetchItemsMetadata = async () => {
    if (!token || currentPath.length === 0) return;

    const category = currentPath[currentPath.length - 1].toLowerCase();
    const bucket = getBucketFromCategory(category);
    const items = getCurrentCategoryItemNames();

    if (!items || items.length === 0) {
      setItemsWithMetadata([]);
      return;
    }

    setMetadataLoading(true);

    try {
      const itemsWithMeta = await Promise.all(
        items.map(async (itemName) => {
          // Check cache first
          const cacheKey = `${bucket}-${itemName}`;
          if (metadataCache.has(cacheKey)) {
            const cachedData = metadataCache.get(cacheKey);
            
            return {
              name: itemName,
              ...metadataCache.get(cacheKey),
            };
          }

          try {
            const metadataResponse = await fetch(DOWNLOAD_ENDPOINTS.METADATA, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                bucket: bucket,
                fileName: itemName,
              }),
              credentials: "include",
            });

            if (metadataResponse.ok) {
              const metadata = await metadataResponse.json();

              const cacheData = {
                size: metadata.size || 0,
                lastModified: metadata.lastModified || new Date().toISOString(),
                contentType: metadata.contentType || "application/octet-stream",
              };
              // Cache the metadata
              setMetadataCache((prev) =>
                new Map(prev).set(cacheKey, cacheData)
              );

              return {
                name: itemName,
                size: metadata.size || 0,
                lastModified: metadata.lastModified || new Date().toISOString(),
                contentType: metadata.contentType || "application/octet-stream",
                ...metadata,
              };
            } else {
              console.warn(`Failed to fetch metadata for ${itemName}`);
              return {
                name: itemName,
                size: 0,
                lastModified: new Date().toISOString(),
                contentType: "application/octet-stream",
              };
            }
          } catch (error) {
            console.warn(`Error fetching metadata for ${itemName}:`, error);
            return {
              name: itemName,
              size: 0,
              lastModified: new Date().toISOString(),
              contentType: "application/octet-stream",
            };
          }
        })
      );

      setItemsWithMetadata(itemsWithMeta);
    } catch (error) {
      console.error("Error fetching items metadata:", error);
      // Fallback: create items without metadata
      const fallbackItems = items.map((itemName) => ({
        name: itemName,
        size: 0,
        lastModified: new Date().toISOString(),
        contentType: "application/octet-stream",
      }));
      setItemsWithMetadata(fallbackItems);
    } finally {
      setMetadataLoading(false);
    }
  };

  // Toggle dropdown
  const toggleDropdown = (index, event) => {
    event.stopPropagation();
    setOpenDropdownIndex(openDropdownIndex === index ? null : index);
  };

  // Handle item click (open dropdown)
  const handleItemClick = (index, event) => {
    event.preventDefault();
    event.stopPropagation();

    // Clear any existing timeout
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }

    // Set a timeout for single click action
    const timeout = setTimeout(() => {
      setOpenDropdownIndex(openDropdownIndex === index ? null : index);
      setClickTimeout(null);
    }, 250); // 250ms delay to detect double-click

    setClickTimeout(timeout);
  };

  const handleItemDoubleClick = (item, category, event) => {
    event.preventDefault();
    event.stopPropagation();

    // Clear the single-click timeout
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }

    // Close dropdown if it's open
    setOpenDropdownIndex(null);

    // Execute the view action (same as clicking "View" in dropdown)
    onViewItem(item.name || item, category);
  };

  // Handle action click and close dropdown
  const handleActionClick = (action, item, category, event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpenDropdownIndex(null);

    if (action === "view") {
      onViewItem(item.name || item, category);
    } else if (action === "remove") {
      onRemoveItem(item.name || item, category);
    } else if (action === "removeFolder") {
      // Handle folder removal - pass the folder info to parent
      handleFolderRemoval(item, category);
    }
  };

  const handleFolderRemoval = (folderItem, category) => {

  
  // Call the parent's folder removal function (no more window.confirm)
  if (onRemoveFolder) {
    onRemoveFolder(folderItem, category);
  } else {
    console.error('onRemoveFolder prop not provided');
  }
};

  const getFolderPath = (folderName) => {
    if (filesCurrentPath.length > 0) {
      return [...filesCurrentPath, folderName].join("/");
    }
    return folderName;
  };

  // Get all files that belong to a folder path (including subfolders)
  const getAllFilesInFolderPath = (folderPath) => {
    if (!endowment || !endowment.selectedItems) {
      return [];
    }

    const category = currentPath[currentPath.length - 1]?.toLowerCase();
    if (!category || category !== "files") {
      return [];
    }

    const allFiles = endowment.selectedItems.files || [];
    const folderPrefix = folderPath.endsWith("/")
      ? folderPath
      : `${folderPath}/`;

    // Find all files that start with this folder path
    return allFiles.filter((filePath) => filePath.startsWith(folderPrefix));
  };

  // Handle folder click within Files category
  const handleFilesFolderClick = (folderName) => {
    setFilesCurrentPath((prev) => [...prev, folderName]);
    setOpenDropdownIndex(null); // Close any open dropdowns
  };

  // Handle back navigation within Files
  const handleFilesBackClick = () => {
    if (filesCurrentPath.length > 0) {
      setFilesCurrentPath((prev) => prev.slice(0, -1));
    } else {
      // Reset files path when going back to main categories
      setFilesCurrentPath([]);
      onBackClick();
    }
  };

  // Reset files path when category changes
  const handleCategoryClick = (newPath) => {
    setFilesCurrentPath([]);
    onCategoryClick(newPath);
  };

  // Get current category item names (without metadata)
  const getCurrentCategoryItemNames = () => {
    if (!endowment || !endowment.selectedItems || currentPath.length === 0) {
      return [];
    }

    const category = String(currentPath[currentPath.length - 1]).toLowerCase();

    if (category === "files") {
      const allFiles = Array.isArray(endowment.selectedItems.files)
        ? [...endowment.selectedItems.files]
        : [];
      const parsedItems = parseFilesIntoFolders(allFiles, filesCurrentPath);

      // Return only the names of files (not folders) for metadata fetching
      return parsedItems.filter((item) => typeof item === "string");
    }

    switch (category) {
      case "passwords":
        return Array.isArray(endowment.selectedItems.passwords)
          ? [...endowment.selectedItems.passwords]
          : [];
      case "banking":
        return Array.isArray(endowment.selectedItems.banking)
          ? [...endowment.selectedItems.banking]
          : [];
      case "blockchain":
        return Array.isArray(endowment.selectedItems.blockchain)
          ? [...endowment.selectedItems.blockchain]
          : [];
      default:
        return [];
    }
  };

  // Get current category items with metadata and apply search/filter
  const getCurrentCategoryItems = () => {
    if (!endowment || !endowment.selectedItems || currentPath.length === 0) {
      return [];
    }

    const category = String(currentPath[currentPath.length - 1]).toLowerCase();

    if (category === "files") {
      const allFiles = Array.isArray(endowment.selectedItems.files)
        ? [...endowment.selectedItems.files]
        : [];
      const parsedItems = parseFilesIntoFolders(allFiles, filesCurrentPath);

      // Create items with metadata for files
      const enrichedItems = parsedItems.map((item) => {
        if (typeof item === "string") {
          // It's a file - find its metadata from the state variable
          const metadata = itemsWithMetadata.find((meta) => meta.name === item);
          return (
            metadata || {
              name: item,
              type: "file",
              size: 0,
              lastModified: new Date().toISOString(),
            }
          );
        } else {
          // It's a folder
          return {
            ...item,
            size: `${item.fileCount} items`,
            lastModified: new Date().toISOString(),
          };
        }
      });

      return applySearchAndFilter(enrichedItems);
    }

    // For non-files categories, use existing logic
    if (metadataLoading || itemsWithMetadata.length === 0) {
      return itemsWithMetadata;
    }

    return applySearchAndFilter(itemsWithMetadata);
  };

  // Get item count for a specific category
  const getCategoryItemCount = (categoryName) => {
    if (!endowment || !endowment.selectedItems) {
      return 0;
    }

    const category = categoryName.toLowerCase();
    switch (category) {
      case "files":
        return Array.isArray(endowment.selectedItems.files)
          ? endowment.selectedItems.files.length
          : 0;
      case "passwords":
        return Array.isArray(endowment.selectedItems.passwords)
          ? endowment.selectedItems.passwords.length
          : 0;
      case "banking":
        return Array.isArray(endowment.selectedItems.banking)
          ? endowment.selectedItems.banking.length
          : 0;
      case "blockchain":
        return Array.isArray(endowment.selectedItems.blockchain)
          ? endowment.selectedItems.blockchain.length
          : 0;
      default:
        return 0;
    }
  };

  // Apply search and filter to items with metadata
  const applySearchAndFilter = (items) => {
    if (!items || !Array.isArray(items)) return [];

    // Apply search
    let filtered = items;
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        removeTxtExtension(item.name).toLowerCase().includes(lowerSearchTerm)
      );
    }

    // Apply sort
    switch (sortMethod) {
      case "name":
        filtered.sort((a, b) => {
          const nameA = removeTxtExtension(a.name).toLowerCase();
          const nameB = removeTxtExtension(b.name).toLowerCase();
          return sortOrder === "asc"
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA);
        });
        break;
      case "date":
        filtered.sort((a, b) => {
          const dateA = new Date(a.lastModified || 0);
          const dateB = new Date(b.lastModified || 0);
          return sortOrder === "asc"
            ? dateA - dateB // Oldest first
            : dateB - dateA; // Newest first
        });
        break;
      case "size":
        filtered.sort((a, b) => {
          const sizeA = a.size || 0;
          const sizeB = b.size || 0;
          return sortOrder === "asc"
            ? sizeA - sizeB // Smallest first
            : sizeB - sizeA; // Largest first
        });
        break;
      default:
        filtered.sort((a, b) => {
          const nameA = removeTxtExtension(a.name).toLowerCase();
          const nameB = removeTxtExtension(b.name).toLowerCase();
          return nameA.localeCompare(nameB);
        });
    }

    return filtered;
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return "Invalid Date";
    }
  };

  // Get item icon details
  const getItemIconDetails = (item, category) => {
    let iconClass, iconColor;

    const categoryStr = String(category).toLowerCase();
    switch (categoryStr) {
      case "files":
        // Handle both item objects and strings
        const itemName = typeof item === "string" ? item : item.name;
        const itemType = typeof item === "object" ? item.type : "file";

        // Check if it's a folder first
        if (itemType === "folder") {
          iconClass = "fa-folder";
          iconColor = "#ffd700";
        } else {
          const extension = itemName.split(".").pop().toLowerCase();
          if (["jpg", "jpeg", "png", "gif", "bmp"].includes(extension)) {
            iconClass = "fa-file-image";
          } else if (["doc", "docx"].includes(extension)) {
            iconClass = "fa-file-word";
          } else if (["xls", "xlsx"].includes(extension)) {
            iconClass = "fa-file-excel";
          } else if (["pdf"].includes(extension)) {
            iconClass = "fa-file-pdf";
          } else if (["mp3", "wav"].includes(extension)) {
            iconClass = "fa-file-audio";
          } else if (["mp4", "avi", "mov"].includes(extension)) {
            iconClass = "fa-file-video";
          } else {
            iconClass = "fa-file-alt";
          }
          iconColor = "#ffd700";
        }
        break;
      case "passwords":
        iconClass = "fa-key";
        iconColor = "#ffd700";
        break;
      case "banking":
        iconClass = "fa-credit-card";
        iconColor = "#00CC5C";
        break;
      case "blockchain":
        iconClass = "fa-link";
        iconColor = "#4B0082";
        break;
      default:
        iconClass = "fa-file";
        iconColor = "#000000";
    }

    return { iconClass, iconColor };
  };

  // Render breadcrumb
  const renderBreadcrumb = () => {
    const isInFiles =
      currentPath.length > 0 &&
      currentPath[currentPath.length - 1].toLowerCase() === "files";

    return (
      <div className="d-flex flex-column mb-3">
        {(currentPath.length > 0 || filesCurrentPath.length > 0) && (
          <div className="mb-2">
            <Button
              variant="outline-light"
              size="sm"
              className="back-button"
              onClick={
                isInFiles && filesCurrentPath.length > 0
                  ? handleFilesBackClick
                  : onBackClick
              }
            >
              <i className="fas fa-arrow-left mr-1"></i>Back
            </Button>
          </div>
        )}

        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li
              className="breadcrumb-item endowment"
              style={{ cursor: "pointer" }}
              onClick={() => handleCategoryClick([])}
            >
              {endowment ? endowment.alias : "Endowment"}
            </li>

            {currentPath.map((item, index) => (
              <li
                key={index}
                className={`breadcrumb-item endowment ${
                  index === currentPath.length - 1 &&
                  filesCurrentPath.length === 0
                    ? "active"
                    : ""
                }`}
                style={{
                  cursor:
                    index === currentPath.length - 1 &&
                    filesCurrentPath.length === 0
                      ? "default"
                      : "pointer",
                }}
                onClick={() => {
                  if (
                    index !== currentPath.length - 1 ||
                    filesCurrentPath.length > 0
                  ) {
                    setFilesCurrentPath([]);
                    handleCategoryClick(currentPath.slice(0, index + 1));
                  }
                }}
                aria-current={
                  index === currentPath.length - 1 &&
                  filesCurrentPath.length === 0
                    ? "page"
                    : undefined
                }
              >
                {item}
              </li>
            ))}

            {/* Add Files folder breadcrumbs */}
            {isInFiles &&
              filesCurrentPath.map((folder, index) => (
                <li
                  key={`folder-${index}`}
                  className={`breadcrumb-item endowment ${
                    index === filesCurrentPath.length - 1 ? "active" : ""
                  }`}
                  style={{
                    cursor:
                      index === filesCurrentPath.length - 1
                        ? "default"
                        : "pointer",
                  }}
                  onClick={() =>
                    index !== filesCurrentPath.length - 1 &&
                    setFilesCurrentPath((prev) => prev.slice(0, index + 1))
                  }
                  aria-current={
                    index === filesCurrentPath.length - 1 ? "page" : undefined
                  }
                >
                  {folder}
                </li>
              ))}
          </ol>
        </nav>
      </div>
    );
  };

  // Render categories with counts
  const renderCategories = () => {
    const categories = ["Files", "Passwords", "Banking", "Blockchain"];

    return (
      <div className="endow-categories-grid">
        {categories.map((category, index) => {
          const { iconClass, iconColor } = getItemIconDetails("", category);
          const itemCount = getCategoryItemCount(category);

          return (
            <div
              key={index}
              className="endow-category-tile"
              data-category={category}
              onClick={() => onCategoryClick([category])}
            >
              <div className="endow-category-icon-container">
                <i
                  className={`fas ${iconClass} endow-category-icon`}
                  style={{ color: iconColor }}
                ></i>
                {itemCount > 0 && (
                  <div className="endow-category-badge">{itemCount}</div>
                )}
              </div>
              <div className="endow-category-name">{category}</div>
              <div className="endow-category-count">
                {itemCount} {itemCount === 1 ? "item" : "items"}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render category items with clickable functionality and metadata
  const renderCategoryItems = () => {
    if (!endowment || !endowment.selectedItems) {
      return (
        <div className="endow-empty-state">
          <i className="fas fa-folder-open"></i>
          <h5>No Items Found</h5>
          <p>Please make sure your endowment is properly configured.</p>
        </div>
      );
    }

    // Show loading state while fetching metadata
    if (metadataLoading) {
      return (
        <div className="endow-empty-state">
          <Spinner animation="border" role="status" variant="primary">
            <span className="sr-only">Loading...</span>
          </Spinner>
          <h5 className="mt-3">Loading item details...</h5>
          <p>Fetching file metadata for better sorting...</p>
        </div>
      );
    }

    const items = getCurrentCategoryItems();

    if (!items || !Array.isArray(items) || items.length === 0) {
      const category =
        currentPath[currentPath.length - 1]?.toLowerCase() || "this category";
      return (
        <div className="endow-empty-state">
          <i className="fas fa-search"></i>
          <h5>No Items Found</h5>
          <p>
            {searchTerm
              ? `No items matching "${searchTerm}" in ${category}.`
              : `There are no items in ${category}. You can add items using the + button.`}
          </p>
        </div>
      );
    }

    return (
      <div className="endow-items-list" ref={dropdownRef}>
        {items.map((item, index) => {
          const category =
            currentPath[currentPath.length - 1]?.toLowerCase() || "";
          
          const { iconClass, iconColor } = getItemIconDetails(item, category);
          const isDropdownOpen = openDropdownIndex === index;

          return (
            <div
              key={index}
              className={`endow-item ${isDropdownOpen ? "active" : ""}`}
              data-item-name={item.name}
              data-item-type={category}
              onClick={(e) => {
                if (e.target.closest(".endow-item-actions")) {
                  return;
                }
                if (item.type === "folder") {
                  handleFilesFolderClick(item.name);
                } else {
                  handleItemClick(index, e);
                }
              }}
              onDoubleClick={(e) => {
                if (item.type === "folder") {
                  handleFilesFolderClick(item.name);
                } else {
                  handleItemDoubleClick(item, category, e);
                }
              }}
              style={{ cursor: "pointer", position: "relative" }}
            >
              <div className="endow-item-content">
                <div className="endow-item-icon">
                  <i
                    className={`fas ${iconClass}`}
                    style={{ color: iconColor, fontSize: "24px" }}
                  ></i>
                </div>
                <div
                  className="endow-item-details"
                  style={{ flex: 1, paddingRight: "10px" }}
                >
                  <div
                    className="endow-item-name text-truncate"
                    title={item.name}
                    style={{ fontWeight: "500" }}
                  >
                    {removeTxtExtension(item.name)}
                  </div>
                  <div
                    className="endow-item-metadata"
                    style={{
                      fontSize: "0.875rem",
                      color: "#6c757d",
                      marginTop: "2px",
                    }}
                  >
                    {item.type === "folder"
                      ? `${item.fileCount} items • ${formatDate(
                          item.lastModified
                        )}`
                      : `${formatFileSize(item.size)} • ${formatDate(
                          item.lastModified
                        )}`}
                  </div>
                </div>
              </div>

              <div className="endow-item-actions">
                <button
                  className="btn btn-link"
                  type="button"
                  onClick={(e) => toggleDropdown(index, e)}
                  style={{ padding: "0.25rem" }}
                >
                  <i
                    className="fas fa-ellipsis-v"
                    style={{ color: "#00CC5C" }}
                  ></i>
                </button>

                {/* Custom dropdown menu */}
                {/* Custom dropdown menu */}
                {isDropdownOpen && (
                  <div
                    className="dropdown-menu show"
                    style={{
                      position: "absolute",
                      right: "0",
                      top: "100%",
                      zIndex: 1500,
                      minWidth: "120px",
                    }}
                  >
                    {item.type !== "folder" ? (
                      <>
                        <a
                          className="dropdown-item view-item"
                          href="#"
                          onClick={(e) =>
                            handleActionClick("view", item, category, e)
                          }
                        >
                          <i className="fas fa-eye fa-sm fa-fw mr-2"></i>View
                        </a>
                        <a
                          className="dropdown-item remove-item"
                          href="#"
                          onClick={(e) =>
                            handleActionClick("remove", item, category, e)
                          }
                        >
                          <i className="fas fa-trash fa-sm fa-fw mr-2"></i>
                          Remove
                        </a>
                      </>
                    ) : (
                      <>
                        <a
                          className="dropdown-item"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenDropdownIndex(null);
                            handleFilesFolderClick(item.name);
                          }}
                        >
                          <i className="fas fa-folder-open fa-sm fa-fw mr-2"></i>
                          Open Folder
                        </a>
                        <a
                          className="dropdown-item"
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpenDropdownIndex(null);
                            handleActionClick(
                              "removeFolder",
                              item,
                              category,
                              e
                            );
                          }}
                        >
                          <i className="fas fa-folder-open fa-sm fa-fw mr-2"></i>
                          Remove Folder
                        </a>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="category-browser">
      {currentPath.length > 0 && (
        <div className="endow-category-header">
          <h5 className="endow-category-title">
            <i
              className={`fas ${
                getItemIconDetails("", currentPath[currentPath.length - 1])
                  .iconClass
              }`}
              style={{
                color: getItemIconDetails(
                  "",
                  currentPath[currentPath.length - 1]
                ).iconColor,
              }}
            ></i>
            {currentPath[currentPath.length - 1]}
            {metadataLoading && (
              <Spinner
                animation="border"
                size="sm"
                className="ml-2"
                style={{ width: "1rem", height: "1rem" }}
              />
            )}
          </h5>

          <div className="endow-category-controls">
            <div className="endow-search-container">
              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => onSearch(e.target.value)}
              />
              <button
                className="endow-search-button"
                onClick={() => onSearch("")}
                title={searchTerm ? "Clear search" : "Search"}
              >
                <i
                  className={`fas ${searchTerm ? "fa-times" : "fa-search"}`}
                ></i>
              </button>
            </div>

            <Dropdown className="endow-sort-dropdown">
              <Dropdown.Toggle variant="light" id="sort-dropdown">
                <i className="fas fa-sort mr-1"></i>
                {sortMethod === "name" && sortOrder === "asc" && "A-Z"}
                {sortMethod === "name" && sortOrder === "desc" && "Z-A"}
                {sortMethod === "date" && sortOrder === "asc" && "Oldest"}
                {sortMethod === "date" && sortOrder === "desc" && "Newest"}
                {sortMethod === "size" && sortOrder === "asc" && "Smallest"}
                {sortMethod === "size" && sortOrder === "desc" && "Largest"}
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item onClick={() => onSort("name", "asc")}>
                  <i className="fas fa-sort-alpha-down mr-2"></i>Name (A-Z)
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onSort("name", "desc")}>
                  <i className="fas fa-sort-alpha-down-alt mr-2"></i>Name (Z-A)
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onSort("date", "asc")}>
                  <i className="fas fa-sort-numeric-down mr-2"></i>Date (Oldest)
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onSort("date", "desc")}>
                  <i className="fas fa-sort-numeric-down-alt mr-2"></i>Date
                  (Newest)
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onSort("size", "asc")}>
                  <i className="fas fa-sort-amount-down mr-2"></i>Size
                  (Smallest)
                </Dropdown.Item>
                <Dropdown.Item onClick={() => onSort("size", "desc")}>
                  <i className="fas fa-sort-amount-down-alt mr-2"></i>Size
                  (Largest)
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      )}

      <div className="category-browser-content justify-content-center">
        {renderBreadcrumb()}

        {currentPath.length === 0 ? renderCategories() : renderCategoryItems()}
      </div>
    </div>
  );
};

export default CategoryBrowser;
